import React, { useState, useEffect } from 'react';
import { Lead } from '../../types/Lead';
import { LeadGenerationAttempt, leadGenerationStatusService } from '../../services/leadGenerationStatusService';
import { InProgressLeadCard } from './InProgressLeadCard';
import LeadCard from './LeadCard';
import { useAuth } from '../../hooks/useAuth';
import { Timestamp } from 'firebase/firestore';
import { useSortedLeads } from '../../hooks/useSortedLeads';

import { Pagination } from '../ui/Pagination';

interface LeadGenerationAttemptsListProps {
  allLeads: Lead[];
  onLeadToggle: (leadId: string) => void;
  expandedCards: Set<string>;
  onLeadUpdate?: (updatedLead: Lead) => void;
  starredLeadIds: Set<string>;
  onStarChange: (leadId: string, isStarred: boolean) => void;
  onMemberChange?: (leadId: string, isMember: boolean) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  itemsPerPage: number;
}

export const LeadGenerationAttemptsList: React.FC<LeadGenerationAttemptsListProps> = ({
  allLeads,
  onLeadToggle,
  expandedCards,
  onLeadUpdate,
  starredLeadIds,
  onStarChange,
  onMemberChange,
  currentPage,
  onPageChange,
  totalPages,
  itemsPerPage
}) => {
  const { user } = useAuth();
  const [attempts, setAttempts] = useState<LeadGenerationAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setAttempts([]);
      setIsLoading(false);
      return;
    }

    const unsubscribe = leadGenerationStatusService.subscribeToUserAttempts(
      user.uid,
      (newAttempts) => {
        setAttempts(newAttempts);
        setIsLoading(false);
      }
    );

    return unsubscribe;
  }, [user]);

  // Separate attempts by status
  const inProgressAttempts = attempts.filter(attempt => attempt.isInProgress);
  const failedAttempts = attempts.filter(attempt => attempt.isFailed);
  const completedAttempts = attempts.filter(attempt => attempt.isCompleted);

  // Get completed leads that match our attempts
  const completedLeads = completedAttempts
    .map(attempt => allLeads.find(lead => lead.id === attempt.leadId))
    .filter((lead): lead is Lead => lead !== undefined)
    // Deduplicate by lead ID to prevent showing the same lead multiple times
    .filter((lead, index, self) =>
      index === self.findIndex(l => l.id === lead.id)
    );

  // Get all leads generated by the current user (including those without status documents)
  // This includes leads from file uploads and manual generation
  const userLeads = allLeads.filter(lead => lead.userId === user?.uid);

  // Separate user leads by their status
  const userCompletedLeads = userLeads.filter(lead =>
    // Lead is completed if it has a status document marked as completed
    completedAttempts.some(attempt => attempt.leadId === lead.id) ||
    // Or if it has processed data (indicating completion)
    (lead.processed && lead.processed.averageScore !== undefined)
  );

  const userInProgressLeads = userLeads.filter(lead =>
    // Lead is in progress if it has a status document marked as in progress
    inProgressAttempts.some(attempt => attempt.leadId === lead.id) ||
    // Or if it doesn't have processed data yet
    (!lead.processed || lead.processed.averageScore === undefined)
  );

  // Create a deduplicated list of all completed leads to avoid double-counting
  const allCompletedLeads = [
    ...completedLeads,
    ...userCompletedLeads.filter(lead =>
      !completedLeads.some(completedLead => completedLead.id === lead.id)
    )
  ];

  const sortedLeads = useSortedLeads(allCompletedLeads);



  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px', color: '#6b7280' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '16px' }}>
          <div style={{ width: '20px', height: '20px', border: '2px solid #e5e7eb', borderTop: '2px solid #0ea5e9', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          Loading your lead generation history...
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (attempts.length === 0 && userLeads.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '800px',
          minHeight: '220px',
          backgroundColor: 'rgba(34, 197, 94, 0.05)',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          border: '2px solid #bbf7d0',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
            opacity: 0.3,
          }}>
            🔍
          </div>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            margin: 0,
          }}>
            No Leads Generated Yet
          </h3>
          <p style={{
            color: '#6b7280',
            fontSize: '16px',
            lineHeight: '1.5',
            margin: 0,
            maxWidth: '300px',
          }}>
            You haven't generated any leads yet. Use the form on the left to create your first lead.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* In Progress Section */}
      {(inProgressAttempts.length > 0 || userInProgressLeads.length > 0) && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #f59e0b'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #f59e0b',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div>
              <div style={{ fontWeight: '600', color: '#92400e', fontSize: '14px' }}>
                PROCESSING ({inProgressAttempts.length + userInProgressLeads.length})
              </div>
              <div style={{ fontSize: '12px', color: '#d97706' }}>
                Lead generation in progress
              </div>
            </div>
          </div>

          <div>
            {/* Show in-progress attempts with status documents */}
            {inProgressAttempts.map((attempt) => (
              <InProgressLeadCard key={attempt.id} attempt={attempt} />
            ))}

            {/* Show in-progress leads without status documents (from file uploads) */}
            {userInProgressLeads
              .filter(lead => !inProgressAttempts.some(attempt => attempt.leadId === lead.id))
              .map((lead) => (
                <InProgressLeadCard
                  key={lead.id || lead.metadata.companyName}
                  attempt={{
                    id: lead.id || lead.metadata.companyName,
                    userId: lead.userId || '',
                    companyName: lead.metadata.companyName,
                    geography: lead.metadata.hqCountry, // Use hqCountry as geography
                    status: 'In Progress',
                    createdAt: lead.createdAt ? Timestamp.fromDate(new Date(lead.createdAt)) : Timestamp.now(),
                    updatedAt: lead.updatedAt ? Timestamp.fromDate(new Date(lead.updatedAt)) : Timestamp.now(),
                    isInProgress: true,
                    isFailed: false,
                    isCompleted: false
                  }}
                />
              ))}
          </div>
        </div>
      )}

      {/* Failed Attempts Section */}
      {failedAttempts.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#fee2e2',
            borderRadius: '8px',
            border: '1px solid #ef4444'
          }}>
            <span style={{ fontSize: '16px' }}>❌</span>
            <div>
              <div style={{ fontWeight: '600', color: '#dc2626', fontSize: '14px' }}>
                FAILED ATTEMPTS ({failedAttempts.length})
              </div>
              <div style={{ fontSize: '12px', color: '#ef4444' }}>
                Lead generation attempts that failed
              </div>
            </div>
          </div>

          <div>
            {failedAttempts.map((attempt) => (
              <InProgressLeadCard key={attempt.id} attempt={attempt} />
            ))}
          </div>
        </div>
      )}

      {/* Completed Leads Section */}
      {sortedLeads.length > 0 && (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#d1fae5',
            borderRadius: '8px',
            border: '1px solid #10b981'
          }}>
            <span style={{ fontSize: '16px' }}>✅</span>
            <div>
              <div style={{ fontWeight: '600', color: '#065f46', fontSize: '14px' }}>
                COMPLETED LEADS ({sortedLeads.length})
              </div>
              <div style={{ fontSize: '12px', color: '#059669' }}>
                Successfully generated leads
              </div>
            </div>
          </div>

          <div>
            {sortedLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                isExpanded={expandedCards.has(lead.id || '')}
                onToggle={() => lead.id && onLeadToggle(lead.id)}
                onLeadUpdate={onLeadUpdate}
                isStarred={lead.id ? starredLeadIds.has(lead.id) : false}
                onStarChange={(isStarred) => lead.id && onStarChange(lead.id, isStarred)}
                onMemberChange={onMemberChange}
              />
            ))}
          </div>

          {/* Pagination */}
          {sortedLeads.length > 0 && (
            <div style={{ marginTop: '48px' }}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                totalItems={sortedLeads.length}
                itemsPerPage={itemsPerPage}
            />
            </div>
          )}
        </div>
      )}



      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};